import React, { useEffect, useState } from "react";
import { Link as Link2 } from "react-router-dom";
import not_found from "../../assets/images/sattva_misc/not_found.jpg"
import { motion } from "framer-motion";

export default function Blog({ h3Heading, text, card_bg }) {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 Convert Google Drive link to usable image URL
  const formatImage = (media = []) => {
    const image = media.find(m => m.type === "image");
    if (!image?.url) return null;

    const url = image.url;

    // Case 1: already a thumbnail URL → return as is
    if (url.includes("drive.google.com/thumbnail")) {
      return url;
    }

    // Case 2: /file/d/FILE_ID/view
    let match = url.match(/\/d\/([^/]+)/);

    // Case 3: ?id=FILE_ID
    if (!match) {
      match = url.match(/[?&]id=([^&]+)/);
    }

    if (match?.[1]) {
      return `https://drive.google.com/thumbnail?id=${match[1]}&sz=w1200`;
    }

    // Fallback (non-drive image)
    return url;
  };



  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch("https://sattva-backend.demetrix.in/blogs");
        const result = await res.json();

        if (result?.status === "success") {
          const filteredBlogs = result?.data
            // ❌ Ignore deleted blogs
            .filter((blog) => !blog.is_deleted)
            // 📅 Sort by created_at (latest first)
            .sort(
              (a, b) =>
                new Date(b.created_at) - new Date(a.created_at)
            )
            // 🧾 Only 3 recent blogs
            .slice(0, 3);

          setBlogs(filteredBlogs);
        } else {
          setBlogs([]);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  if (loading) {
    return (
      <section className="py-16">
        <div className="container">
          {/* Header shimmer */}
          <div className="text-center mb-8">
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-20 mx-auto mb-2 animate-pulse"></div>
            <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-48 mx-auto mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-72 mx-auto animate-pulse"></div>
          </div>

          {/* Blog cards shimmer */}
          <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-6">
            {[1, 2, 3].map((_, index) => (
              <div
                key={index}
                className="rounded-md shadow shadow-slate-200 dark:shadow-slate-800 overflow-hidden flex flex-col h-full animate-pulse"
              >
                {/* Image placeholder */}
                <div className="w-full h-48 bg-gray-200 dark:bg-slate-700" />

                {/* Content placeholder */}
                <div className="p-6 flex flex-col flex-grow gap-3">
                  <div className="h-5 bg-gray-200 dark:bg-slate-700 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-5/6"></div>
                  <div className="mt-auto h-4 bg-gray-200 dark:bg-slate-700 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16" id="blog">
      <div className="container">
        <div className="grid grid-cols-1 text-center">
          {/* <h6 className="text-blue-600 text-base font-medium uppercase mb-2">
            Blogs
          </h6> */}
          <h3 className="mb-4 md:text-2xl text-xl font-medium dark:text-white">
            {h3Heading}
          </h3>
          <p className="text-slate-500 dark:text-slate-300 max-w-xl mx-auto">
            {text}
          </p>
        </div>
        {
          blogs?.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-6 mt-8">
            {blogs?.map((item, index) => {
              const image = formatImage(item.media);
              // console.log(image)
              return (
                <div
                  key={index}
                  className={`blog relative rounded-md shadow shadow-slate-200 dark:shadow-slate-800 overflow-hidden ${card_bg} flex flex-col h-full`}
                >
                  {/* IMAGE */}
                  {image ? (
                    <img
                      src={image}
                      alt={item.title}
                      className="w-full h-48 object-cover"
                      loading="lazy"
                      referrerPolicy="no-referrer"
                      onError={(e) => {
                        e.currentTarget.src = "/placeholder.jpg"; // optional fallback
                      }}
                    />
                  ) : (
                    <div className="h-48 bg-gray-200 flex items-center justify-center text-sm text-gray-500">
                      No image
                    </div>
                  )}

                  {/* CONTENT */}
                  <div className="p-6 flex flex-col flex-grow">
                    <Link2
                      to={`/blogs/${item.slug}`}
                      className="text-lg font-medium hover:text-green-600 dark:text-white dark:hover:text-green-600 transition-all"
                    >
                      {item.title}
                    </Link2>

                    {/* SUMMARY – 3 lines + ellipsis */}
                    <p className="text-slate-500 mt-3 line-clamp-3 leading-relaxed">
                      {item.summary}
                    </p>

                    {/* READ MORE – fixed bottom-left */}
                    <div className="mt-auto pt-5">
                      <Link2
                        to={`/blogs/${item.slug}`}
                        className="btn btn-link hover:text-blue-600 dark:hover:text-blue-600 after:bg-blue-600 dark:text-white transition duration-500"
                      >
                        Read More <i className="uil uil-arrow-right"></i>
                      </Link2>
                    </div>
                  </div>
                </div>

              );
            })}
          </div>
            : <div className="min-h-[65vh] w-full flex items-center justify-center px-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-sm p-8 max-w-md w-full text-center"
              >
                {/* Illustration */}
                <motion.img
                  src={not_found}
                  alt="No blogs"
                  className="mx-auto h-40 mb-6"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* Text */}
                <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">
                  No blogs found
                </h3>

                <p className="text-slate-500 dark:text-slate-400 mb-6">
                  We couldn’t find any blogs here. Once content is published, it’ll show up right away.
                </p>

                {/* CTA */}
                {/* <div className="flex justify-center gap-3">
                  <Link
                    to="/"
                    className="px-5 py-2.5 rounded-lg text-sm font-medium
              bg-slate-900 text-white dark:bg-white dark:text-slate-900
              hover:opacity-90 transition"
                  >
                    Go Home
                  </Link>

                  <Link
                    to="/contact"
                    className="px-5 py-2.5 rounded-lg text-sm font-medium
              border border-slate-300 dark:border-slate-600
              text-slate-700 dark:text-slate-200
              hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  >
                    Contact Us
                  </Link>
                </div> */}
              </motion.div>
            </div>
        }
      </div>
    </section>
  );
}
